import { StampsProxyService } from '@addins/core/journal';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { SagaObject } from '@models/imported/SagaBase/SagaObject';
import { CStampFreeText } from '@models/imported/SagaSchema/CStampFreeText';
import { ModalService } from '@services/modal/modal.service';
import { Tool, ToolsProviderService } from '@services/tools';
import { TranslatedAlertService } from '@services/translated-alert/translated-alert.service';
import { lastValueFrom } from 'rxjs';
import { StampTextEditorComponent } from '../../components/stamp-text-editor/stamp-text-editor.component';

enum FreeTextType {
  Report = 2,
  RequestReport = 3,
  CR = 5,
  ImportantFact = 6
}

@Injectable({
  providedIn: 'root'
})
export class FreetextToolService {
  private uniqueFreeTextStampTypes: number[] = null;

  constructor(
    private toolsProvider: ToolsProviderService,
    private translatedAlert: TranslatedAlertService,
    private modalController: ModalController,
    private modalService: ModalService,
    private stampsProxy: StampsProxyService
  ) {}

  registerTool() {
    const editFreeTextTool = Tool.createTool({
      text: 'Edit',
      execute: async context => {
        if (context[0] instanceof CStampFreeText) {
          const stamp: CStampFreeText = context[0];
          this.editFreeText(stamp);
        }
      },
      icon: 'create'
    });

    this.toolsProvider.register(toolsProvider => {
      if (toolsProvider.context.length === 1 && toolsProvider.contextObjectsAre(CStampFreeText)) {
        toolsProvider.addTool(editFreeTextTool);
      }
    });
  }

  private editFreeText(stamp: CStampFreeText) {
    this.showStampFreeTextEditor(
      this.getFreeTextStampLabel(stamp.StampType),
      text => this.updateFreeText(stamp.SourceComponentUid, text),
      stamp.Text
    );
  }

  private showStampFreeTextEditor(title: string, resultHandler: (text: string) => Promise<boolean>, initialText?: string) {
    this.modalController
      .create({
        component: StampTextEditorComponent,
        componentProps: { title: title, resultHandler: resultHandler.bind(this), initialText: initialText }
      })
      .then(modal => modal.present());
  }

  getFreeTextStampLabel(stampType: number): string {
    return `Mobile.${CStampFreeText.$t}_${stampType}`;
  }

  private updateFreeText(objGuid: string, text: string): Promise<boolean> {
    return this.modalService
      .presentLoading()
      .then(() =>
        lastValueFrom(this.stampsProxy.readFreetext(objGuid)).then(freeText => {
          freeText.Text = text;
          return lastValueFrom(this.stampsProxy.updateFreetext(freeText));
        })
      )
      .finally(() => this.modalService.dismissLoading());
  }

  addFreeText(parentId: string, freeTextType: FreeTextType) {
    const title: string = this.getFreeTextStampLabel(freeTextType);

    this.getUniqueFreeTextStampTypes().then(uniqueFreeTextStampTypes => {
      if (uniqueFreeTextStampTypes.includes(freeTextType)) {
        this.createUniqueFreeTextStamp(parentId, title, freeTextType);
      } else {
        this.showStampFreeTextEditor(title, text => this.createFreeText(parentId, text, freeTextType));
      }
    });
  }

  private getUniqueFreeTextStampTypes(): Promise<number[]> {
    return this.uniqueFreeTextStampTypes !== null
      ? Promise.resolve(this.uniqueFreeTextStampTypes)
      : this.modalService.presentLoading().then(() =>
          lastValueFrom(this.stampsProxy.getUniqueFreeStampTypes())
            .then(uniqueFreeTextStampTypes => (this.uniqueFreeTextStampTypes = uniqueFreeTextStampTypes))
            .finally(() => this.modalService.dismissLoading())
        );
  }

  private createFreeText(parentId: string, text: string, type: number): Promise<boolean> {
    return this.modalService
      .presentLoading()
      .then(() => lastValueFrom(this.stampsProxy.addFreetext(parentId, text, type)))
      .finally(() => this.modalService.dismissLoading());
  }

  private createUniqueFreeTextStamp(parentId: string, title: string, freeTextType: number) {
    this.getFreeTextStamp(parentId, freeTextType).then(freeTextStamp => {
      if (!freeTextStamp) {
        this.showStampFreeTextEditor(title, text => this.createFreeText(parentId, text, freeTextType));
      } else {
        this.translatedAlert
          .show({
            header: 'Mobile.ConfirmUpdate',
            message: 'Mobile.StampAlreadyExists',
            buttons: [
              {
                text: 'Mobile.No',
                role: 'cancel',
                value: false
              },
              {
                text: 'Mobile.Yes',
                value: true
              }
            ]
          })
          .then(confirmed => {
            if (confirmed) {
              this.showStampFreeTextEditor(title, text => this.updateFreeText(freeTextStamp.SourceComponentUid, text), freeTextStamp.Text);
            }
          });
      }
    });
  }

  private getFreeTextStamp(callcardId: string, freeTextType: number): Promise<CStampFreeText> {
    return this.modalService
      .presentLoading()
      .then(() =>
        lastValueFrom(this.stampsProxy.getStamps(callcardId)).then(
          stamps =>
            stamps
              .reverse()
              .find(
                (stamp: SagaObject) => stamp.$t == CStampFreeText.$t && (stamp as CStampFreeText).StampType == freeTextType
              ) as CStampFreeText
        )
      )
      .finally(() => this.modalService.dismissLoading());
  }
}
